/**
 * The console's single Zustand store. Field names and shapes follow
 * docs/design-handoff/README.md "State management" verbatim — that section
 * is the contract Tasks 13-20 build the real surfaces against, so nothing
 * here should be renamed without updating it there too.
 *
 * This module owns state only. Every state change that should actually take
 * effect against the backend goes through `ops.call` (src/lib/ops.ts) or
 * arrives from `subscribeStream` (src/lib/stream.ts) — the store itself
 * never talks to the network.
 */
import { create } from 'zustand'
import type { Line, StaffRole, ThreadSummary, ThemeChoice } from '@communicator/shared'
import type { StaffIdentity } from './lib/identity'
import { TIMEZONE_DEFAULT, THEME_CHOICE_DEFAULT } from '@communicator/shared'
import { applyTheme } from './lib/theme'

export type { StaffIdentity } from './lib/identity'

/**
 * Whether the console believes it is signed in as staff — task 31.
 * `'checking'` is the bootstrap-in-flight state, before
 * `identity.whoAmI()` (src/lib/identity.ts) has resolved even once; the auth
 * gate (src/components/auth/AuthGate.tsx) deliberately does NOT redirect to
 * sign-in during this state, so a signed-in visitor never sees a sign-in
 * flash while that first round trip is still in the air. `bootstrapConsole()`
 * (src/bootstrap.ts) is the only place this moves out of 'checking', and
 * SignInPage/InitialsMenu's sign-out are the only other writers.
 *
 * This is a genuinely separate axis from the five-tier client confidence
 * model (`ThreadSummary.tier`) — see communicatorv2.3.md §5: "Staff auth is
 * entirely separate from client identity... The five-tier confidence model
 * does not apply to staff." Nothing here shares a name, a value, or any
 * comparison logic with `tier`.
 */
export type AuthStatus = 'checking' | 'signed-in' | 'signed-out'

/**
 * How long a toast stays up.
 *
 * Andrew asked for about three seconds on the coverage notice. The design
 * handoff says 4.2s, which nothing has ever actually done — going with the
 * number somebody using it asked for.
 */
export const TOAST_TTL_MS = 3000

export type QueueFilter = 'queue' | 'all' | 'mine'
export type CtxTab = 'context' | 'ai'
export type Page = null | 'settings' | 'history' | 'transcript'
export type ModalKind = null | 'ticket' | 'match' | 'handoff' | 'close' | 'otp' | 'compose'
export type HistoryFilter = 'everything' | 'closed' | 'live' | 'chat' | 'sms' | 'calls'
export type HandInternalState = 'live-warm' | 'scheduled'
export type OtpMethod = 'sms' | 'email'

export interface Peer {
  id: string
  name: string
  role: StaffRole
  covering: boolean
  /**
   * Has the console open right now (a live /ops/stream connection).
   *
   * Not the same as `covering`, which is a deliberate "I am at the desk"
   * declaration — someone can be online without covering, or covering with the
   * laptop shut. Optional so an older ops build reads as absent rather than
   * everyone appearing offline.
   */
  online?: boolean
  /**
   * Has the console open AND is at the machine — Slack's active/away.
   *
   * `online` is a fact about a socket; this is a claim about a person. A locked
   * laptop with the console open reported itself online for five hours, so the
   * hand-off list offered somebody who was not there. Anything asking "can they
   * take this now" wants this field.
   *
   * Optional for the same reason as `online`: an older ops build reads as
   * absent, and absent is treated as away rather than as present.
   */
  active?: boolean
  typingThreadId: string | null
  readingThreadId: string | null
}

export interface ServiceStatus {
  relay: boolean
  halo: boolean
  sms: boolean
  voice: boolean
}

export type ToastTone = 'ok' | 'warn' | 'bad' | 'info'

export interface Toast {
  id: string
  text: string
  tone: ToastTone
}

export interface DayHours {
  on: boolean
  from?: string
  to?: string
}

export type WeekHours = Record<'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', DayHours>

/**
 * `connecting` — first attempt, nothing received yet.
 * `live` — the feed is open; `open` has fired.
 * `reconnecting` — dropped, retrying with backoff. The pane is stale.
 * `closed` — deliberately torn down (sign-out, unmount). Never retried.
 */
export type StreamStatus = 'connecting' | 'live' | 'reconnecting' | 'closed'
export interface ConsoleState {
  /**
   * The signed-in staff member, once a real session resolves — task 27
   * (console integration). Not part of docs/design-handoff/README.md's
   * "State management" list (that section predates any identity work;
   * tasks 13/15 both flagged the gap rather than guess at a shape), added
   * here because it is the one field every "is this thread mine"
   * comparison across the app needs a single real source for. `null` is the
   * honest "identity not established yet" state — see
   * src/lib/identity.ts's own header comment for why that is the case in
   * Phase 1 and what starts it flowing for real.
   */
  me: StaffIdentity | null

  /** See the `AuthStatus` type's own doc comment, just above. */
  authStatus: AuthStatus

  // Role and coverage
  role: StaffRole
  admin: boolean
  covering: boolean
  /**
   * Whether this person IS the SuperAdmin (users-and-permissions design
   * §1/§3) — never locally settable, same rule as `admin` just above. Seeded
   * only from a real `GET /ops/whoami` (bootstrap.ts) or the sign-in verify
   * response (SignInPage.tsx), both of which read it fresh off the `staff`
   * row on every request. The Users pane (`components/settings/
   * UsersSection.tsx`) is the one place this matters: only the SuperAdmin
   * may grant or revoke admin
   * (@communicator/shared's `canChangeAdmin` rule), and the console cannot
   * decide whether to offer that control without knowing this about itself.
   */
  superAdmin: boolean

  // Queue rail (pane A)
  filter: QueueFilter
  techFilter: string | null
  techMenu: boolean
  railHover: boolean
  /** Arrivals queued while the pointer is inside the rail; all placed together on releaseHeld(). */
  held: ThreadSummary[]
  /** Thread ids currently animating in (cc-slide) after being placed. */
  arrived: string[]
  /**
   * The call floor's manually-set open/closed preference (task 3, Tossker
   * repaint) — docs/design-handoff/tossker-redesign/README.md §2 "Call
   * floor". The README proposes an auto-collapse timer after ten minutes
   * empty; that was cut (task-3 brief, binding decision 1: "ten minutes is
   * invented, and a rail that rearranges itself on an invisible clock reads
   * as a glitch"). Collapse is manual, via the chevron the collapsed strip
   * itself carries — this field is exactly that toggle, nothing more.
   *
   * This is NOT the floor's actual rendered open/closed state — CallFloor.tsx
   * computes that as `floorOpen || <a ringing or live call exists>`, so a
   * ringing/live call always forces the floor open regardless of what this
   * flag says, and the flag simply governs what happens once no call is live
   * any more. The strip itself never disappears either way (see
   * rail/format.ts's `callFloorCollapsedLabel` and CallFloor.tsx) — a zone
   * that appears only when the phone rings would shove queue rows under the
   * pointer, exactly what the rail's hold-on-hover guard exists to prevent.
   */
  floorOpen: boolean

  // Selection and composer
  activeId: string | null
  /**
   * Unsent text, KEYED BY THREAD.
   *
   * This was one string for the whole console, and it behaved exactly as one
   * string does: whatever you were typing showed up under the next
   * conversation you clicked. Andrew typed a call write-up on one call and
   * found it sitting in another's box — "I didn't type that in treeline, but
   * when I switched cards in the left the text followed me."
   *
   * A draft belongs to the conversation it was typed in. Switching away keeps
   * it; switching back restores it; it never appears anywhere else. Held in
   * memory only — a reload is a fresh start, which is the existing behaviour
   * and not what was being complained about.
   *
   * `drafts` is the message composer, `callNotes` the call write-up box above
   * it. Two surfaces, two independent drafts on the same thread, because a
   * half-typed text and a half-typed write-up are different things.
   */
  drafts: Record<string, string>
  callNotes: Record<string, string>
  stackOpen: string | null

  // Context + AI pane (pane C)
  ctxOpen: boolean
  ctxTab: CtxTab

  // Shell
  theme: ThemeChoice
  page: Page
  settingsTab: string

  // Search and history
  q: string
  searchOpen: boolean
  transcriptId: string | null
  hFilter: HistoryFilter

  // Modals
  modal: ModalKind
  ticketNum: string
  closeNote: string
  closeCue: string | null
  handTech: string | null
  handMode: HandInternalState | null
  handCue: string | null
  otpSent: boolean
  otpCode: string
  otpMethod: OtpMethod | null
  pendingHandoff: boolean
  /**
   * What the Compose modal opens pre-filled with — the ticket it was launched
   * from (Attach-ticket-style flows) and/or the contact already on hand, so
   * the modal never has to ask ComposeModal's caller to remember both. `null`
   * fields are the honest "nothing to prefill from here" case (e.g. opened
   * from a bare "New message" button with no ticket or contact context),
   * distinct from the whole object being `null`, which means the modal is
   * not open at all. Set by `openCompose`, cleared by `closeModal` along with
   * `modal` itself.
   *
   * `error` (finding 4): the deep-link prefetch's own honest failure, distinct from `error`
   * state inside ComposeModal itself (a `start-outbound` call that failed) — this one is set
   * by `useComposeFromLink` when the ticket's compose-prefill lookup came back `wired: false`,
   * so the composer can say WHY the contact never showed up, or why the ticket chip was
   * cleared, instead of silently doing either. `null` is "no prefetch problem to report" —
   * the ordinary case, including every non-deep-link open of this modal.
   */
  composePrefill: {
    ticketId: string | null
    contact: { contactId: string; name: string } | null
    error: string | null
  } | null

  // Server-sourced data
  svc: ServiceStatus
  /** heldWriteCount per threadId while Halo is unreachable. */
  sync: Record<string, number>
  threads: ThreadSummary[]
  peers: Peer[]
  cfg: Record<string, unknown>
  /**
   * Whether the live feed is actually connected. Published because a feed that
   * is quietly retrying is indistinguishable on screen from one that works —
   * which is how a dead stream went unnoticed until someone refreshed.
   */
  streamStatus: StreamStatus
  /** Env-backed field status from GET /ops/config, keyed by field id. */
  cfgEnv: Record<string, { envVar: string; set: boolean; value?: string }>
  /**
   * This person's own preferences (the p.* fields), from GET /ops/preferences.
   * Kept apart from `cfg` because they are per-user: folding them together is
   * how one tech's notification sound would have silenced everyone's.
   */
  prefs: Record<string, unknown>
  /**
   * Whether `prefs` has actually been answered by the server yet, as opposed
   * to being the empty object it starts as.
   *
   * `prefs['p.theme']` is `undefined` in BOTH "still loading" and "loaded, no
   * preference stored", and conflating those two shipped a real bug: the
   * theme effect read `undefined` on mount, concluded there was no
   * preference, and painted the default — stomping the theme the inline
   * `<head>` script had already applied correctly, so every load flashed
   * Legacy Night until preferences arrived. A reader that must not act on a
   * guess needs to know the difference.
   *
   * Deliberately NOT set when the fetch fails: a failed read is not an
   * answer, and painting the default over someone's remembered theme because
   * the network blinked is exactly the behaviour this flag exists to prevent.
   */
  prefsLoaded: boolean
  hours: WeekHours | null
  logoDark: string | null
  logoLight: string | null
  secretEdit: string | null
  secretDraft: string

  toasts: Toast[]

  /**
   * The configured SMS lines (task 7, multiple SMS lines) — staff-readable
   * via `ops.lines()` (`GET /ops/lines`), fetched once per session alongside
   * `cfg`/`prefs` in the same post-sign-in effect (see bootstrap.ts's
   * "Config" section). `null` is the honest "not loaded yet, or the fetch
   * failed" state, deliberately indistinguishable from each other: a reader
   * gating on this (`(s.lines?.length ?? 0) > 1`) degrades to rendering no
   * line labels either way rather than guessing — the same
   * degraded-never-broken shape as `cfg`/`prefs` failing to load. Never `[]`
   * on a failed fetch, which would read as "zero lines configured" instead
   * of "don't know."
   */
  lines: Line[] | null
}

export interface ConsoleActions {
  /** Generic escape hatch for simple field updates the named actions below don't cover. */
  set: (partial: Partial<ConsoleState>) => void

  /** The one place a real signed-in identity lands — see the `me` field's own doc comment. */
  /** Set this thread's composer draft. Never touches any other thread's. */
  setDraft: (threadId: string, text: string) => void
  /** Set this thread's call write-up draft. Never touches any other thread's. */
  setCallNote: (threadId: string, text: string) => void

  setMe: (me: StaffIdentity | null) => void

  /** See the `AuthStatus` type's own doc comment. */
  setAuthStatus: (status: AuthStatus) => void

  setTheme: (theme: ThemeChoice) => void
  openModal: (modal: Exclude<ModalKind, null>) => void
  /** Opens the Compose modal with whatever prefill the caller has on hand — see `composePrefill`'s
   * own doc comment. A field omitted from `prefill` (or `prefill` omitted entirely) lands as `null`,
   * never `undefined`, so every reader can rely on the object always being fully shaped. */
  openCompose: (prefill?: {
    ticketId?: string | null
    contact?: { contactId: string; name: string } | null
    error?: string | null
  }) => void
  closeModal: () => void
  selectThread: (id: string | null) => void

  setThreads: (threads: ThreadSummary[]) => void
  upsertThread: (thread: ThreadSummary) => void
  removeThread: (id: string) => void

  setPeers: (peers: Peer[]) => void
  setSvc: (svc: Partial<ServiceStatus>) => void
  setSync: (threadId: string, heldWriteCount: number) => void

  holdArrival: (thread: ThreadSummary) => void
  releaseHeld: () => void
  markArrived: (id: string) => void
  clearArrived: (id: string) => void

  pushToast: (toast: { text: string; tone?: ToastTone; id?: string; ttlMs?: number }) => string
  dismissToast: (id: string) => void

  setCfg: (values: Record<string, unknown>) => void
  setCfgEnv: (env: Record<string, { envVar: string; set: boolean; value?: string }>) => void
  setPrefs: (prefs: Record<string, unknown>) => void
  setPref: (id: string, value: unknown) => void
  setStreamStatus: (status: StreamStatus) => void

  /** See the `lines` field's own doc comment. Wholesale replacement, same shape as `setPeers`. */
  setLines: (lines: Line[] | null) => void

  reset: () => void
}

export type ConsoleStore = ConsoleState & ConsoleActions

export const initialConsoleState: ConsoleState = {
  me: null,
  authStatus: 'checking',

  role: 'dispatch',
  admin: false,
  covering: false,
  superAdmin: false,

  filter: 'queue',
  techFilter: null,
  techMenu: false,
  railHover: false,
  held: [],
  arrived: [],
  floorOpen: true,

  activeId: null,
  drafts: {},
  callNotes: {},
  stackOpen: null,

  ctxOpen: true,
  ctxTab: 'context',

  theme: THEME_CHOICE_DEFAULT,
  page: null,
  settingsTab: 'personal',

  q: '',
  searchOpen: false,
  transcriptId: null,
  hFilter: 'everything',

  modal: null,
  ticketNum: '',
  closeNote: '',
  closeCue: null,
  handTech: null,
  handMode: null,
  handCue: null,
  otpSent: false,
  otpCode: '',
  otpMethod: null,
  pendingHandoff: false,
  composePrefill: null,

  svc: { relay: true, halo: true, sms: true, voice: true },
  sync: {},
  threads: [],
  peers: [],
  cfg: {},
  streamStatus: 'connecting',
  cfgEnv: {},
  prefs: {},
  prefsLoaded: false,
  hours: null,
  logoDark: null,
  logoLight: null,
  secretEdit: null,
  secretDraft: '',

  toasts: [],

  lines: null,
}

let toastSeq = 0
function nextToastId(): string {
  toastSeq += 1
  return `toast-${Date.now()}-${toastSeq}`
}

export const useStore = create<ConsoleStore>((set) => ({
  ...initialConsoleState,

  set: (partial) => set(partial),

  setDraft: (threadId, text) =>
    set((s) => ({ drafts: { ...s.drafts, [threadId]: text } })),
  setCallNote: (threadId, text) =>
    set((s) => ({ callNotes: { ...s.callNotes, [threadId]: text } })),

  setMe: (me) => set({ me }),
  setAuthStatus: (status) => set({ authStatus: status }),

  setTheme: (theme) => {
    applyTheme(theme)
    set({ theme })
  },

  openModal: (modal) => set({ modal }),
  openCompose: (prefill) =>
    set({
      modal: 'compose',
      composePrefill: {
        ticketId: prefill?.ticketId ?? null,
        contact: prefill?.contact ?? null,
        error: prefill?.error ?? null,
      },
    }),
  closeModal: () => set({ modal: null, composePrefill: null }),
  // Opening a thread reads it. Cleared here rather than waiting for the next
  // list fetch, so the marker goes out the moment you look — the beacon
  // records the same fact server-side for the next load and other devices.
  selectThread: (id) =>
    set((s) => ({
      activeId: id,
      threads: id ? s.threads.map((t) => (t.id === id && t.unread ? { ...t, unread: false } : t)) : s.threads,
    })),

  // A wholesale replacement (a filter/tech-scope fetch resolving) can race a
  // fresh arrival that got queued into `held` during the fetch's async gap.
  // If the incoming list already contains that id, drop it from `held` so
  // releaseHeld() doesn't place a second copy; if the incoming list does not
  // contain it (it belongs to a different scope), it survives in `held`
  // untouched.
  setThreads: (incoming) =>
    set((s) => {
      // KEEP WHAT THE SERVER CANNOT KNOW.
      //
      // `unread` is per viewer, and this replaces the whole list — including
      // from the presence tick (routes/stream.ts's `runPresenceTick`), which
      // lists threads with NO readerId and broadcasts one payload to every
      // console. So every tick arrived carrying no unread at all and wiped
      // every marker on the rail.
      //
      // That is the bug behind "the pulse goes away after about 2 seconds",
      // and the variable timing was the tell: the tick runs on its own clock,
      // so a message lands somewhere in that cycle and the next tick clears
      // it — sometimes two seconds later, sometimes ten.
      //
      // PRESERVED, not re-derived. A snapshot means "here is the list", not
      // "something happened", so inventing unread here would light up every
      // client-last thread on every tick, including ones already read. The
      // derive belongs in upsertThread, which handles actual events.
      const previous = new Map(s.threads.map((t) => [t.id, t.unread]))
      const threads = incoming.map((t) =>
        t.unread === undefined && previous.has(t.id) ? { ...t, unread: previous.get(t.id) } : t,
      )

      if (s.held.length === 0) return { threads }
      const incomingIds = new Set(threads.map((t) => t.id))
      const survivingHeld = s.held.filter((h) => !incomingIds.has(h.id))
      return survivingHeld.length === s.held.length ? { threads } : { threads, held: survivingHeld }
    }),
  // A thread currently sitting in `held` (queued behind the rail's arrival
  // pill) is not in `threads` — an update for it must land back in `held` in
  // place, never appended to `threads`, or releaseHeld() would place a
  // second, stale copy alongside the one this update already produced.
  upsertThread: (thread) =>
    set((s) => {
      // UNREAD IS PER PERSON, AND THE STREAM IS A BROADCAST.
      //
      // `GET /ops/threads` computes `unread` for the caller, but an SSE thread
      // event is one payload sent to every subscriber, so it cannot carry a
      // per-viewer flag and does not try. Two consequences, both bugs until
      // this: a reply arriving live never lit the marker (it only appeared
      // after a refetch), and worse, the incoming payload OVERWROTE a marker
      // that was already showing.
      //
      // So derive it here when the server did not say: a client-last thread
      // that is not the one open in front of me is unread. Opening it clears
      // the flag (see selectThread) and the presence beacon records the read
      // server-side, which is what makes it agree across devices.
      const existing = s.threads.find((t) => t.id === thread.id)?.unread

      // STICKY UNTIL YOU OPEN IT HERE. Andrew: "it should pulse indefinitely
      // until the thread is opened."
      //
      // A local `true` now outlives anything the server says, because the
      // server's answer can be false for a reason that is nothing to do with
      // this person's phone: the read marker is written by the presence
      // beacon, so a laptop with that thread selected reports it read every
      // fifteen seconds — and the next resync on the phone quietly dropped the
      // dot for somebody who had not looked at it.
      //
      // The server still decides on a FRESH load, where there is no local
      // state to be sticky about; that is what keeps a genuinely-read thread
      // from being marked unread on every device forever. Only opening it
      // here clears it (see selectThread).
      const derived =
        existing === true
          ? true
          : (thread.unread ??
            (thread.lastDirection === 'them' && thread.id !== s.activeId ? true : existing))
      const incoming = { ...thread, unread: derived }

      // THE STORE HOLDS WHAT IT IS TOLD; THE RAIL DECIDES WHAT TO SHOW.
      //
      // Scope filtering used to happen here, dropping a thread that had left
      // the current tab. That is a DISPLAY rule enforced on shared state, and
      // it broke the pane: `ThreadPane` reads live updates through
      // `storeSummary`, so a thread dropped from the store stopped receiving
      // them — which is exactly the defect test/context.test.tsx's "task-42
      // regression" cases exist to catch, and they caught it.
      //
      // The rail filters at render instead (QueueRail's `visible`), which
      // needs no exceptions: the open conversation keeps its live updates
      // because it is still in the store, and it is still absent from a rail
      // it does not belong in because the rail never renders it.
      const heldIdx = s.held.findIndex((h) => h.id === incoming.id)
      if (heldIdx !== -1) {
        const nextHeld = s.held.slice()
        nextHeld[heldIdx] = { ...nextHeld[heldIdx], ...incoming }
        return { held: nextHeld }
      }
      const idx = s.threads.findIndex((t) => t.id === incoming.id)
      // Newly matching (a claim released, say) is an ARRIVAL, not a silent
      // append — but only if it belongs here, which the guard above decided.
      if (idx === -1) return { threads: [...s.threads, incoming] }
      const next = s.threads.slice()
      next[idx] = { ...next[idx], ...incoming }
      return { threads: next }
    }),
  // Also drops the id from `held` — a thread closed while queued behind the
  // arrival pill must not be resurrected when releaseHeld() next fires.
  removeThread: (id) =>
    set((s) => ({
      threads: s.threads.filter((t) => t.id !== id),
      held: s.held.filter((h) => h.id !== id),
    })),

  setPeers: (peers) => set({ peers }),
  setSvc: (svc) => set((s) => ({ svc: { ...s.svc, ...svc } })),
  setSync: (threadId, heldWriteCount) => set((s) => ({ sync: { ...s.sync, [threadId]: heldWriteCount } })),

  // A queue, not a single slot: a second arrival while one is already held
  // must queue behind it rather than bypass the hold and render under the
  // cursor. Re-holding an id already queued (e.g. a rapid duplicate arrival)
  // updates that entry in place instead of duplicating it.
  holdArrival: (thread) =>
    set((s) => {
      const idx = s.held.findIndex((h) => h.id === thread.id)
      if (idx !== -1) {
        const next = s.held.slice()
        next[idx] = { ...next[idx], ...thread }
        return { held: next }
      }
      return { held: [...s.held, thread] }
    }),
  releaseHeld: () =>
    set((s) => {
      if (s.held.length === 0) return {}
      const placed = s.held
      return { held: [], threads: [...s.threads, ...placed], arrived: [...s.arrived, ...placed.map((t) => t.id)] }
    }),
  markArrived: (id) => set((s) => (s.arrived.includes(id) ? {} : { arrived: [...s.arrived, id] })),
  clearArrived: (id) => set((s) => ({ arrived: s.arrived.filter((a) => a !== id) })),

  pushToast: (toast) => {
    const id = toast.id ?? nextToastId()
    set((s) => ({ toasts: [...s.toasts, { id, text: toast.text, tone: toast.tone ?? 'info' }] }))

    // THE TIMER LIVES HERE, not with the caller.
    //
    // Toast.tsx documented it as "the timer lives with whoever calls
    // dismissToast" — and nothing ever did. Four places raise toasts and none
    // of them dismissed one, so every toast this console has ever shown stayed
    // on screen until a refresh. A design that depends on each caller
    // remembering is a design that will be forgotten, and was.
    //
    // `ttlMs: 0` opts out for anything that must be dismissed deliberately.
    // Nothing does today; it exists so the next such case does not have to
    // reinstate the old trap.
    const ttl = toast.ttlMs ?? TOAST_TTL_MS
    if (ttl > 0) {
      setTimeout(() => {
        useStore.getState().dismissToast(id)
      }, ttl)
    }
    return id
  },
  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  setCfg: (values) => set({ cfg: values }),
  setCfgEnv: (cfgEnv) => set({ cfgEnv }),
  // Marks prefs as answered: see the `prefsLoaded` field's own doc comment.
  setPrefs: (prefs) => set({ prefs, prefsLoaded: true }),
  setPref: (id, value) => set((s) => ({ prefs: { ...s.prefs, [id]: value } })),
  setStreamStatus: (streamStatus) => set({ streamStatus }),

  setLines: (lines) => set({ lines }),

  reset: () => set(initialConsoleState),
}))

/**
 * The configured display zone (`v.tz`), for every timestamp a human reads.
 *
 * One reader, so a deployment that has never touched the setting — or has
 * blanked it — gets Mountain time rather than an empty string. `Intl` throws on
 * `""`, and a formatter that throws would take a whole pane down over a
 * cosmetic value; the shared helpers degrade to the raw ISO string instead, but
 * the honest fix is to never hand them a bad zone in the first place.
 */
export function selectTimeZone(cfg: Record<string, unknown>): string {
  const tz = cfg['v.tz']
  return typeof tz === 'string' && tz.trim().length > 0 ? tz : TIMEZONE_DEFAULT
}

/**
 * A per-user preference, falling back to the schema default.
 *
 * One reader so every consumer agrees on what "unset" means. `p.*` fields
 * default to true, and a person who has never touched the toggle should get
 * that default rather than `undefined` read as off.
 */
export function selectPref<T>(prefs: Record<string, unknown>, id: string, fallback: T): T {
  const value = prefs[id]
  return value === undefined || value === null ? fallback : (value as T)
}
